import {
  IConfiguration,
  IEditObjectConfiguration,
  IWorkflow,
  IWorkflowId,
  IWorkflowState,
  IObjectTypeConfigurationItem,
  IViewConfigurationItem,
} from "../../backend/interfaces/configuration/IConfiguration";
import { IItem, IHierarchyItem } from "../../backend/interfaces/IItem";
import { createSelector } from "reselect";
import { getHierarchyOfItems, filterHierarchyRecursive } from "../../backend/helpers/itemHelpers";
import { iRootState } from "../store";
import { IQuickFilter, IMfilesPropertyFilter, IFilterGroup, IFilter, FilterGroupConditionEnum } from "../../backend/interfaces/filters/IFilter";
import IMFilesPropertyDefinition from "../../backend/interfaces/mfiles/IMFilesPropertyDefinition";
import { filterItem, createFilter, quickFilterItem } from "../../backend/helpers/filteringHelper";
import sortBy from "lodash/sortBy";
import uniq from "lodash/uniq";
import { mapDataById } from "./helpers";
import { deduceLinks } from "./computations";
import { IFilterValueListItem } from "../../backend/interfaces/filters/IFilterValueListItem";
import { ITEM_DEFAULT_COLOR } from "src/gantt/helpers/Const";
import { translateWorkflowName, translateStateName } from "../../backend/helpers/translationHelpers";
import { FilterOperator } from "src/backend/constants/filterOperators";

export const configurationSelector = (state: iRootState): IConfiguration => state.common.configuration;
export const objectTypeConfigurationSelector = createSelector(
  configurationSelector,
  (configuration: IConfiguration) => configuration.objectTypeConfigurations
);

export const allDataForViewsSelector = createSelector(
  (state: iRootState) => state.common.configuration,
  (state: iRootState) => state.UI.displaySettings,
  (state: iRootState) => state.items.allData,
  (configuration: IConfiguration, displaySettings, allData: IItem[]) => {
    return configuration.viewConfigurations.map((viewConfiguration) => {
      const objectTypeIdsOfView = Array.from(viewConfiguration.objectHierarchy.keys());
      const filteredData = allData.filter((item) => objectTypeIdsOfView.includes(item.objectType));
      return filteredData.map((item) => ({ ...item, color: displaySettings.stateInfo[item.status]?.color ?? ITEM_DEFAULT_COLOR }));
    });
  }
);

export const enabledPanelFiltersForViewsSelector = createSelector(
  (state: iRootState) => state.filters.panelFilters,
  (state: iRootState) => state.filters.enabledPanelFilters,
  (state: iRootState) => state.filters.temporaryFilters,
  (filterGroups: IFilterGroup[][], enabledFilters: number[][], temporaryFilterGroups: IFilterGroup[]): IFilterGroup[][] => {
    return enabledFilters.map((indexList: number[], viewIndex: number) => {
      const temporaryFilterOfView = temporaryFilterGroups[viewIndex] ? [temporaryFilterGroups[viewIndex]] : [];
      const enabledFiltersOfView = filterGroups[viewIndex]?.filter((_, i) => indexList.includes(i)) ?? [];
      return [...temporaryFilterOfView, ...enabledFiltersOfView];
    });
  }
);

// Gets hierarchical filtered memoized datasets for views
export const hierarchicalDataForViewsSelector = createSelector(
  (state: iRootState) => state.common.configuration,
  (state: iRootState) => allDataForViewsSelector(state as never),
  (state: iRootState) => state.filters.quickFilters,
  enabledPanelFiltersForViewsSelector,
  (
    configuration: IConfiguration,
    viewDataSets: IItem[][],
    quickFilters: IQuickFilter[],
    enabledFilterGroupsOfViews: IFilterGroup[][]
  ) => {
    return configuration.viewConfigurations.map((vc, index) => {

      const allDataOfView    = viewDataSets[index];
      const hierarchicalData = getHierarchyOfItems(vc, allDataOfView);
      const allDataMap       = new Map(allDataOfView.map(item => [item.id, item]));
      const hierarchicalMap  = new Map(hierarchicalData.map(item => [item.id, item]));

      const quickSearchCondition = quickFilters[index]?.value;

      const quickSearchFiltersOfType = new Map(Array.from(configuration.objectTypeConfigurations.values()).map(objTypeConfig => 
        [objTypeConfig.objectTypeId, objTypeConfig.quickSearch.map(q => createFilter(`quick filter ${q}`, q, FilterOperator.Like, quickSearchCondition))]));

      // Apply enabled panel filters to data
      const filterFunc  = (item : IHierarchyItem) => visibleItemsIds.includes(item.id);
      const sortByFunc = (item : IHierarchyItem) => visibleItemsIds.indexOf(item.id);

      const enabledFilterGroups = enabledFilterGroupsOfViews[index] ?? [];

      let panelfilteredAllDataOfView: IItem[] = [];

      if ( enabledFilterGroups?.length ) {

        // filter each filterGroup separately
        const filterResultsArray: IItem[][] = [];

        for ( const filterGroup of enabledFilterGroups ) {
          const filterResult = filterAllDataOfView( allDataOfView, filterGroup, allDataMap, hierarchicalMap, quickSearchCondition, quickSearchFiltersOfType );
          filterResultsArray.push(filterResult);
        }

        // const mergedFilterResults = [].concat.apply([], filterResultsArray ); // olrder browsers
        const mergedFilterResults = filterResultsArray.flat(1);

        //?? (Group1) OR (Group2) OR ... OR (GroupN)
        // Remove possible duplicates objects
        const uniqueFilterResults = mergedFilterResults.filter((item: IItem, position: number, array: IItem[]) => {
          return array.map((mapItem: IItem) => mapItem.id ).indexOf(item.id) === position;
        }) as IItem[];

        panelfilteredAllDataOfView = uniqueFilterResults;

        //?? (Group1) AND (Group2) AND ... AND (GroupN) mock
        // IF we take only common elemens from X array(s) that would be AND search on top level
        /* 
        if ( filterResultsArray?.length > 0 ) {

          filterResultsArray.sort(function(a, b) { // sort shortest array to be first
            return a.length - b.length;
          });

          const matchingFilterResults = filterResultsArray.reduce((p,c) => p.filter(e => c.includes(e)));
        } */

      } else {
        panelfilteredAllDataOfView = filterAllDataOfView( allDataOfView, null, allDataMap, hierarchicalMap, quickSearchCondition, quickSearchFiltersOfType );
      }

      const visibleItemsIds = panelfilteredAllDataOfView.map(i => i.id);

      // Determine visible items in the hierarchy.
      // Include items that pass filters and also their parents recursively.
      const finalHierarchy = filterHierarchyRecursive(hierarchicalData, filterFunc, sortByFunc);
      const dataMap       = mapDataById(finalHierarchy);
      
      return deduceLinks(configuration.linkConfigurations, dataMap);
    });
  }
);


const getCombinedFilters = (filters: IFilter[]): Map<number, IMfilesPropertyFilter[]> => {

  const combinedFilters = new Map<number,IMfilesPropertyFilter[]>();

  for (const f of filters) {
    const filter = (f as IMfilesPropertyFilter);

    if(!combinedFilters.has( filter.propertyId)) {
      combinedFilters.set( filter.propertyId, []);
    }

    const combinedFilter = combinedFilters.get( filter.propertyId );
    combinedFilter.push( filter );
  }

  return combinedFilters;
}


const filterAllDataOfView = (
  allDataOfView: IItem[], 
  filterGroup: IFilterGroup, 
  allDataMap: Map<number, IItem>,
  hierarchicalMap: Map<number, IHierarchyItem>,
  quickSearchCondition: string,
  quickSearchFiltersOfType: Map<number, IFilter[]> ): IItem[] => {

  return allDataOfView.filter(item => {

    const hierarchyItem     = hierarchicalMap.get(item.id);
    const hierarchyChildren = hierarchyItem.children.map(i => allDataMap.get(i));

    if ( filterGroup?.filters ) {

      let isMatch = false;

      switch (filterGroup?.conditionType) {

        case FilterGroupConditionEnum.AND:

          isMatch = checkFiltersMatchAll(item, hierarchyChildren, filterGroup);
          break;

        case FilterGroupConditionEnum.OR:
          
          isMatch = checkFiltersMatchAny(item, hierarchyChildren, filterGroup);
          break;
      
        default:
          console.log("This filter group condition type has not been implemented.");
          break;
      }

      if (!isMatch) {
        return false;
      }
    }

    // quick filter from search text input
    if ( quickSearchCondition?.length > 0 ) {

      let searchFound = false;
      const quickSearchFilters = quickSearchFiltersOfType.get(item.objectType);

      for (const quickSearchFilter of quickSearchFilters) {
        if(!searchFound && quickFilterItem(item, quickSearchFilter)) {
          searchFound = true;
        }
      }

      for (const child of hierarchyChildren) {
        const childSuickSearchFilters = quickSearchFiltersOfType.get(child.objectType);
        for(const quickSearchFilter of childSuickSearchFilters) {
          if(!searchFound && quickFilterItem(child, quickSearchFilter)) {
            searchFound = true;
          }
        }
      }

      if (!searchFound) {
        return false;
      }
    }

    return true; // default if no filters are set
  });
}


const checkFiltersMatchAll = (item: IItem, hierarchyChildren: IItem[], filterGroup: IFilterGroup): boolean => {

  const matchTable: { filterName: string, match: boolean }[] = [];
  const matchMap = new Map<string, boolean>();

  for ( const f of filterGroup?.filters ) {
   
    if ( filterItem(item, f) === true ) {
      matchTable.push({ filterName: f.name, match: true })

      matchMap.set( f.name, true );
    } else {

      //TODO do we really need for cycle here ? is there a better way?
      let childMatched = false; // if one child has matched we can continue checking the other filters

      for (const child of hierarchyChildren) {
        if (!childMatched && filterItem(child, f) === true) {
          matchTable.push({ filterName: f.name, match: true })
          break; // probably enough to know if filter is a match on at least 1 children
        }
      }
    }
  }

  let matchCount = 0;
  
  matchTable.forEach(item => {
    if ( item.match === true ) {
      matchCount++ 
    }
  });

  return matchCount === filterGroup?.filters.length;
}



const checkFiltersMatchAny = (item: IItem, hierarchyChildren: IItem[], filterGroup: IFilterGroup): boolean => {

  let filterMatched = false;

  for ( const f of filterGroup?.filters ) {

    if ( filterItem(item, f) === true ) {

      filterMatched = true;
      break;

    } else {

      for (const child of hierarchyChildren) {
        if ( filterItem(child, f) === true) {
          filterMatched = true;
          break;
        }
      }

      if ( filterMatched ) { // break from group
        break;
      }
    }
  }

  return filterMatched;
}

export const propertyDefinitionsOfFilterPanelsForViewsSelector = createSelector(
  (state: iRootState) => state.common.configuration,
  (configuration: IConfiguration): IMFilesPropertyDefinition[][] => {
    return configuration.viewConfigurations.map((viewConfiguration) => {
      const objectTypesOfView = Array.from(viewConfiguration.objectHierarchy.keys());
      const propertyDefinitionIdsDistinct = uniq(
        objectTypesOfView.flatMap((id) => configuration.associatedPropertyDefinitionsOfObjectTypes.get(id))
      );
      const propertyDefinitions = propertyDefinitionIdsDistinct.map((id) => configuration.propertyDefinitions.get(id));
      return sortBy(propertyDefinitions, (p) => p.Name);
    });
  }
);

export const editConfigOfItemToAddSelector = createSelector(
  (state: iRootState) => state.items.allData,
  (state: iRootState) => state.items.selectedItem,
  (state: iRootState) => state.common.configuration,
  (allData: IItem[], selectedItemId: number | null, configuration: IConfiguration) => {
    if (selectedItemId == null) {
      const objectTypeConfiguration: IObjectTypeConfigurationItem = configuration.objectTypeConfigurations.values().next().value; // First object in configuration. ES6 Map retains insertion order.
      return objectTypeConfiguration.editConfig;
    } else {
      const selectedItem = allData[selectedItemId];
      const parentEditConfig = configuration.objectTypeConfigurations.get(selectedItem.objectType)?.editConfig;
      if (parentEditConfig && parentEditConfig.childObjectTypesAndProperties.length > 0) {
        const [childObjectType, _] = parentEditConfig.childObjectTypesAndProperties[0];
        return configuration.objectTypeConfigurations.get(childObjectType)?.editConfig;
      } else {
        return null;
      }
    }
  }
);

export const editConfigOfSelectedItemSelector = createSelector(
  (state: iRootState) => state.items.allData,
  (state: iRootState) => state.items.selectedItem,
  (state: iRootState) => state.common.configuration,
  (allData: IItem[], selectedItemId: number, configuration: IConfiguration) =>
    configuration.objectTypeConfigurations.get(allData[selectedItemId]?.objectType)?.editConfig
);

export const mayEditSelectedItemSelector = createSelector(
  (state: iRootState) => state.items.allData,
  (state: iRootState) => state.items.selectedItem,
  (state: iRootState) => state.common.configuration,
  (allData: IItem[], selectedItemId: number, configuration: IConfiguration): boolean =>
    configuration.objectTypeConfigurations.get(allData[selectedItemId]?.objectType)?.mayEdit === true
);

export const workflowOfItemToAddSelector = createSelector(
  (state: iRootState) => editConfigOfItemToAddSelector(state as never),
  (state: iRootState) => state.common.configuration.workflows,
  (editConfig: IEditObjectConfiguration, workflows: Map<IWorkflowId, IWorkflow>) => workflows.get(editConfig?.workflowId)
);

export const workflowOfSelectedItemSelector = createSelector(
  (state: iRootState) => editConfigOfSelectedItemSelector(state as never),
  (state: iRootState) => state.common.configuration.workflows,
  (editConfig: IEditObjectConfiguration, workflows: Map<IWorkflowId, IWorkflow>) => workflows.get(editConfig?.workflowId)
);

export const workflowStatesForFiltersSelector = createSelector(
  (state: iRootState) => state.common.configuration.workflows,
  (state: iRootState) => state.common.language,
  (workflowMap: Map<IWorkflowId, IWorkflow>, _language: string): IFilterValueListItem[] => {
    const workflows = Array.from(workflowMap.values());
    const workflowNameToState = workflows.flatMap((w) => w.States.map((ws) => ({ workflowName: translateWorkflowName(w.ID, w.Name), state: ws })));
    return workflowNameToState.map(({ workflowName, state }) => ({ value: state.ID, displayName: `${workflowName} - ${translateStateName(state.ID, state.Name)}` }));
  }
);

export const workflowStatesSelector = createSelector(
  (state: iRootState) => state.common.configuration.workflows,
  (workflowMap: Map<IWorkflowId, IWorkflow>): IWorkflowState[] => {
    return Array.from(workflowMap.values()).flatMap((workflow) => workflow.States);
  }
);

export const objectTypesOfViewsSelector = createSelector(
  (state: iRootState) => state.common.configuration.viewConfigurations,
  (viewConfigurations: IViewConfigurationItem[]): Map<number, number[]> =>
    new Map(viewConfigurations.map((v, index) => [index, Array.from(v.objectHierarchy.keys())]))
);

export const allObjectTypesSelector = createSelector(
  objectTypeConfigurationSelector,
  (objectTypeConfigurations: Map<number, IObjectTypeConfigurationItem>): number[] => Array.from(objectTypeConfigurations.keys())
);
